/* =====================================================================
   AstaLab 2.0 — xlsxParser.js (round alpha.2, obiettivo 5)

   Porting VERBATIM del parser XLSX vendorizzato della baseline
   1.3.0-beta.6 (astalab.html, sezione "parser XLSX vendorizzato",
   hardening 1.3.0-beta.1) — decoder DEFLATE grezzo (RFC 1951), lettore
   della central directory ZIP, parser XML via DOMParser nativo. Nessuna
   riga di logica cambiata: stesso comportamento byte-per-byte contro le
   stesse fixture reali (vedi tests/test_catalog_golden_130.js).
   =====================================================================*/
import { toStr } from "./normalizeUtils.js";

/* =====================================================================
   Archivio statistiche multistagione — parser XLSX vendorizzato
   (obiettivo A dell'hardening 1.3.0-beta.1)
   =====================================================================
   Implementazione locale, senza alcuna dipendenza da CDN/rete: un decoder DEFLATE grezzo (RFC 1951,
   nessun header zlib/gzip — quello usato dalle voci compresse di un archivio ZIP), un lettore della
   central directory ZIP, e un parser delle parti XML rilevanti di un file .xlsx (workbook.xml,
   sharedStrings.xml, sheetN.xml) via DOMParser (nativo sia in ogni browser reale sia in jsdom — mai
   un parser XML scritto a mano). Validato contro le 4 fixture reali del bundle (i tre file
   Quotazioni/Statistiche piu' l'archivio storico unificato, che usa un namespace SpreadsheetML con
   prefisso esplicito <x:sheet> invece di quello di default <sheet> — da cui l'uso sistematico di
   getElementsByTagNameNS() invece di getElementsByTagName(), altrimenti la seconda forma non
   troverebbe nulla). Tutte le funzioni operano solo su Uint8Array/DataView/TextDecoder/DOMParser:
   nessuna API specifica di Node, cosi' lo stesso codice gira identico sotto jsdom (test
   deterministici) e in un browser reale (Puppeteer/uso reale). */
function XlsxBitReader(bytes){
  this.bytes = bytes; this.pos = 0; this.bitBuf = 0; this.bitCnt = 0;
}
XlsxBitReader.prototype.getBit = function(){
  if(this.bitCnt===0){ this.bitBuf = this.bytes[this.pos++]; this.bitCnt = 8; }
  const bit = this.bitBuf & 1; this.bitBuf >>>= 1; this.bitCnt--; return bit;
};
XlsxBitReader.prototype.getBits = function(n){
  let v = 0;
  for(let i=0;i<n;i++) v |= this.getBit() << i;
  return v>>>0;
};
XlsxBitReader.prototype.align = function(){ this.bitBuf = 0; this.bitCnt = 0; };

function xlsxBuildHuffman(lengths){
  const MAXBITS = 15;
  const counts = new Array(MAXBITS+1).fill(0);
  for(let i=0;i<lengths.length;i++) counts[lengths[i]]++;
  counts[0]=0;
  const offsets = new Array(MAXBITS+2).fill(0);
  for(let i=1;i<=MAXBITS;i++) offsets[i+1] = offsets[i] + counts[i];
  const symbols = new Array(lengths.length).fill(0);
  for(let i=0;i<lengths.length;i++){
    if(lengths[i]) symbols[offsets[lengths[i]]++] = i;
  }
  return {counts, symbols};
}
function xlsxDecodeSymbol(br, huff){
  let code = 0, first = 0, index = 0;
  for(let len=1; len<=15; len++){
    code |= br.getBit();
    const count = huff.counts[len];
    if(code - first < count) return huff.symbols[index + (code - first)];
    index += count; first += count; first <<= 1; code <<= 1;
  }
  throw new Error("XLSX: codice Huffman non valido durante la decompressione");
}

const XLSX_LEN_BASE = [3,4,5,6,7,8,9,10,11,13,15,17,19,23,27,31,35,43,51,59,67,83,99,115,131,163,195,227,258];
const XLSX_LEN_EXTRA = [0,0,0,0,0,0,0,0,1,1,1,1,2,2,2,2,3,3,3,3,4,4,4,4,5,5,5,5,0];
const XLSX_DIST_BASE = [1,2,3,4,5,7,9,13,17,25,33,49,65,97,129,193,257,385,513,769,1025,1537,2049,3073,4097,6145,8193,12289,16385,24577];
const XLSX_DIST_EXTRA = [0,0,0,0,1,1,2,2,3,3,4,4,5,5,6,6,7,7,8,8,9,9,10,10,11,11,12,12,13,13];
const XLSX_CLEN_ORDER = [16,17,18,0,8,7,9,6,10,5,11,4,12,3,13,2,14,1,15];

var __xlsxFixedLit = null, __xlsxFixedDist = null;
function xlsxFixedTrees(){
  if(__xlsxFixedLit) return;
  const litLen = new Array(288);
  for(let i=0;i<144;i++) litLen[i]=8;
  for(let i=144;i<256;i++) litLen[i]=9;
  for(let i=256;i<280;i++) litLen[i]=7;
  for(let i=280;i<288;i++) litLen[i]=8;
  __xlsxFixedLit = xlsxBuildHuffman(litLen);
  const distLen = new Array(30).fill(5);
  __xlsxFixedDist = xlsxBuildHuffman(distLen);
}
function xlsxInflateBlockData(br, litTree, distTree, out){
  for(;;){
    const sym = xlsxDecodeSymbol(br, litTree);
    if(sym < 256){ out.push(sym); continue; }
    if(sym === 256) return;
    const li = sym - 257;
    if(li<0 || li>=29) throw new Error("XLSX: simbolo di lunghezza fuori range durante la decompressione");
    const length = XLSX_LEN_BASE[li] + br.getBits(XLSX_LEN_EXTRA[li]);
    const dsym = xlsxDecodeSymbol(br, distTree);
    if(dsym<0 || dsym>=30) throw new Error("XLSX: simbolo di distanza fuori range durante la decompressione");
    const dist = XLSX_DIST_BASE[dsym] + br.getBits(XLSX_DIST_EXTRA[dsym]);
    let start = out.length - dist;
    if(start<0) throw new Error("XLSX: distanza oltre l'inizio dell'output durante la decompressione");
    for(let i=0;i<length;i++) out.push(out[start+i]);
  }
}
/* DEFLATE grezzo (RFC 1951): blocchi non compressi (BTYPE 00), Huffman fisso (01), Huffman
   dinamico (10) — gli unici tre tipi previsti dallo standard, usati indifferentemente da qualunque
   generatore ZIP conforme (incluso quello che produce i file .xlsx di questo bundle). */
function xlsxInflateRaw(bytes){
  const br = new XlsxBitReader(bytes);
  const out = [];
  xlsxFixedTrees();
  for(;;){
    const bfinal = br.getBit();
    const btype = br.getBits(2);
    if(btype===0){
      br.align();
      const len = bytes[br.pos] | (bytes[br.pos+1]<<8);
      br.pos += 4; // LEN(2) + NLEN(2, complemento — non riverificato: la ZIP central directory e' l'autorità sulla dimensione)
      for(let i=0;i<len;i++) out.push(bytes[br.pos++]);
    } else if(btype===1){
      xlsxInflateBlockData(br, __xlsxFixedLit, __xlsxFixedDist, out);
    } else if(btype===2){
      const hlit = br.getBits(5)+257;
      const hdist = br.getBits(5)+1;
      const hclen = br.getBits(4)+4;
      const clLengths = new Array(19).fill(0);
      for(let i=0;i<hclen;i++) clLengths[XLSX_CLEN_ORDER[i]] = br.getBits(3);
      const clTree = xlsxBuildHuffman(clLengths);
      const allLengths = new Array(hlit+hdist).fill(0);
      let i=0;
      while(i < hlit+hdist){
        const sym = xlsxDecodeSymbol(br, clTree);
        if(sym<16){ allLengths[i++]=sym; }
        else if(sym===16){ const rep = br.getBits(2)+3; const prev = allLengths[i-1]; for(let r=0;r<rep;r++) allLengths[i++]=prev; }
        else if(sym===17){ const rep = br.getBits(3)+3; for(let r=0;r<rep;r++) allLengths[i++]=0; }
        else { const rep = br.getBits(7)+11; for(let r=0;r<rep;r++) allLengths[i++]=0; }
      }
      const litLengths = allLengths.slice(0,hlit);
      const distLengths = allLengths.slice(hlit);
      const litTree = xlsxBuildHuffman(litLengths);
      const distTree = xlsxBuildHuffman(distLengths);
      xlsxInflateBlockData(br, litTree, distTree, out);
    } else {
      throw new Error("XLSX: tipo di blocco DEFLATE non valido (dato corrotto)");
    }
    if(bfinal) break;
  }
  return Uint8Array.from(out);
}

/* Central directory ZIP: mai un parsing "a scansione lineare" delle local file header (che un file
   creato ad-hoc potrebbe manipolare) — si parte sempre dalla End Of Central Directory, l'unica
   struttura la cui posizione e' definita dallo standard indipendentemente dal contenuto compresso. */
function xlsxReadZipEntries(bytes){
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let eocd = -1;
  const searchStart = Math.max(0, bytes.length - 65557);
  for(let i=bytes.length-22; i>=searchStart; i--){
    if(dv.getUint32(i, true) === 0x06054b50){ eocd = i; break; }
  }
  if(eocd<0) throw new Error("XLSX: struttura ZIP non valida (End Of Central Directory non trovata)");
  const cdCount = dv.getUint16(eocd+10, true);
  const cdOffset = dv.getUint32(eocd+16, true);
  const entries = {};
  let p = cdOffset;
  for(let i=0;i<cdCount;i++){
    const sig = dv.getUint32(p, true);
    if(sig !== 0x02014b50) throw new Error("XLSX: voce della central directory ZIP non valida");
    const method = dv.getUint16(p+10, true);
    const compSize = dv.getUint32(p+20, true);
    const uncompSize = dv.getUint32(p+24, true);
    const nameLen = dv.getUint16(p+28, true);
    const extraLen = dv.getUint16(p+30, true);
    const commentLen = dv.getUint16(p+32, true);
    const localHeaderOffset = dv.getUint32(p+42, true);
    const nameBytes = bytes.subarray(p+46, p+46+nameLen);
    const name = new TextDecoder("utf-8").decode(nameBytes);
    entries[name] = {method, compSize, uncompSize, localHeaderOffset};
    p += 46 + nameLen + extraLen + commentLen;
  }
  return {entries, dv, bytes};
}
function xlsxReadZipEntryData(zip, name){
  const e = zip.entries[name];
  if(!e) return null;
  const dv = zip.dv;
  const lp = e.localHeaderOffset;
  if(dv.getUint32(lp, true) !== 0x04034b50) throw new Error("XLSX: local file header ZIP non valido per "+name);
  const nameLen = dv.getUint16(lp+26, true);
  const extraLen = dv.getUint16(lp+28, true);
  const dataStart = lp + 30 + nameLen + extraLen;
  const compData = zip.bytes.subarray(dataStart, dataStart+e.compSize);
  if(e.method===0) return compData.slice();
  if(e.method===8) return xlsxInflateRaw(compData);
  throw new Error("XLSX: metodo di compressione ZIP "+e.method+" non supportato per "+name);
}

/* Namespace-aware ovunque: alcuni generatori XLSX dichiarano il namespace SpreadsheetML con un
   prefisso esplicito (<x:workbook>, <x:sheet>, <x:row>...) invece di quello di default senza
   prefisso (<workbook>, <sheet>...) — getElementsByTagName("sheet") non troverebbe mai <x:sheet> (il
   nome qualificato e' letteralmente diverso). getElementsByTagNameNS() con l'URI del namespace
   risolve entrambi i casi identicamente, qualunque sia il prefisso scelto dal generatore (caso
   riprodotto: AstaLab_Archivio_Storico_Unificato.xlsx usa <x:sheet>, i tre file Quotazioni/
   Statistiche usano <sheet> senza prefisso — lo stesso parser deve leggere correttamente entrambi). */
const XLSX_NS_MAIN = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
const XLSX_NS_RELS_DOC = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";
const XLSX_NS_RELS_PKG = "http://schemas.openxmlformats.org/package/2006/relationships";

function xlsxColLetterToIndex(letters){
  let n=0;
  for(let i=0;i<letters.length;i++) n = n*26 + (letters.charCodeAt(i)-64);
  return n-1;
}
function xlsxParseSharedStrings(xmlText){
  if(!xmlText) return [];
  const doc = new DOMParser().parseFromString(xmlText, "application/xml");
  const items = Array.from(doc.getElementsByTagNameNS(XLSX_NS_MAIN, "si"));
  return items.map(si=>{
    const tNodes = si.getElementsByTagNameNS(XLSX_NS_MAIN, "t");
    const rNodes = si.getElementsByTagNameNS(XLSX_NS_MAIN, "r");
    if(tNodes.length && rNodes.length===0) return tNodes[0].textContent;
    // testo con formattazione mista (piu' nodi <r><t>...</t></r>): concatena tutti i <t>.
    return Array.from(tNodes).map(t=>t.textContent).join("");
  });
}
function xlsxParseWorkbookSheets(xmlText, relsText){
  const doc = new DOMParser().parseFromString(xmlText, "application/xml");
  const sheetNodes = Array.from(doc.getElementsByTagNameNS(XLSX_NS_MAIN, "sheet"));
  const relMap = {};
  if(relsText){
    const relsDoc = new DOMParser().parseFromString(relsText, "application/xml");
    Array.from(relsDoc.getElementsByTagNameNS(XLSX_NS_RELS_PKG, "Relationship")).forEach(r=>{
      relMap[r.getAttribute("Id")] = r.getAttribute("Target");
    });
  }
  return sheetNodes.map(s=>{
    const rId = s.getAttributeNS(XLSX_NS_RELS_DOC, "id") || s.getAttribute("r:id");
    return { name: s.getAttribute("name"), rId, target: relMap[rId] || null };
  });
}
function xlsxParseSheetXML(xmlText, sharedStrings){
  const doc = new DOMParser().parseFromString(xmlText, "application/xml");
  const rowNodes = Array.from(doc.getElementsByTagNameNS(XLSX_NS_MAIN, "row"));
  const rows = [];
  rowNodes.forEach(rowNode=>{
    const cells = Array.from(rowNode.getElementsByTagNameNS(XLSX_NS_MAIN, "c"));
    const rowArr = [];
    cells.forEach(c=>{
      const ref = c.getAttribute("r") || "";
      const m = ref.match(/^([A-Z]+)/);
      const colIdx = m ? xlsxColLetterToIndex(m[1]) : rowArr.length;
      const type = c.getAttribute("t");
      const vNode = c.getElementsByTagNameNS(XLSX_NS_MAIN, "v")[0];
      const isNode = c.getElementsByTagNameNS(XLSX_NS_MAIN, "is")[0];
      let value = null;
      if(type==="s"){
        const idx = vNode ? parseInt(vNode.textContent,10) : -1;
        value = sharedStrings[idx]!=null ? sharedStrings[idx] : "";
      } else if(type==="inlineStr"){
        const tNode = isNode ? isNode.getElementsByTagNameNS(XLSX_NS_MAIN, "t")[0] : null;
        value = tNode ? tNode.textContent : "";
      } else if(type==="str" || type==="e"){
        value = vNode ? vNode.textContent : "";
      } else if(type==="b"){
        value = vNode ? (vNode.textContent==="1") : false;
      } else {
        // numerico (le date XLSX come numero seriale non ricorrono nelle colonne di questo schema)
        value = vNode ? parseFloat(vNode.textContent) : null;
      }
      while(rowArr.length < colIdx) rowArr.push(null);
      rowArr[colIdx] = value;
    });
    rows.push(rowArr);
  });
  return rows;
}

/* Firma ZIP/XLSX: 'PK\x03\x04' (o, per un archivio vuoto/degenere, 'PK\x05\x06') — obiettivo A:
   riconoscimento tramite estensione, MIME E firma, mai solo uno dei tre. */
function xlsxLooksLikeZipSignature(bytes){
  if(!bytes || bytes.length<4) return false;
  return bytes[0]===0x50 && bytes[1]===0x4b && (bytes[2]===0x03||bytes[2]===0x05) && (bytes[3]===0x04||bytes[3]===0x06);
}
const XLSX_MIME_TYPES = ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet","application/vnd.ms-excel","application/zip","application/x-zip-compressed"];
/* Classifica un file da importare come "xlsx" o "csv" — MAI in base alla sola estensione: la firma
   PK (quando i byte sono gia' disponibili) e' sempre l'autorita' finale, cosi' un file rinominato con
   estensione sbagliata non viene comunque letto come testo (obiettivo A: "non leggere mai un XLSX
   come testo CSV"). */
function classifyImportFile(name, mimeType, bytes){
  const lowerName = toStr(name,"").toLowerCase();
  const extSuggestsXlsx = /\.xlsx?$/.test(lowerName);
  const mimeSuggestsXlsx = XLSX_MIME_TYPES.includes(toStr(mimeType,"").toLowerCase());
  if(bytes && bytes.length>=4){
    return xlsxLooksLikeZipSignature(bytes) ? "xlsx" : "csv";
  }
  return (extSuggestsXlsx || mimeSuggestsXlsx) ? "xlsx" : "csv";
}

/* Punto d'ingresso: bytes (Uint8Array, l'intero file) -> {sheetNames:[...], sheets:{name: rows[][]}}.
   Legge il foglio "Tutti" quando esiste, altrimenti il chiamante sceglie il primo foglio non vuoto
   (vedi pickPrimarySheet()) — mai un parsing "a tentativi" di più fogli insieme. */
function parseXLSXWorkbook(bytes){
  if(!xlsxLooksLikeZipSignature(bytes)) throw new Error("Il file non è un XLSX valido (firma ZIP/PK non trovata).");
  const zip = xlsxReadZipEntries(bytes);
  const dec = new TextDecoder("utf-8");
  const workbookEntry = xlsxReadZipEntryData(zip, "xl/workbook.xml");
  if(!workbookEntry) throw new Error("Il file non è un XLSX valido (xl/workbook.xml assente).");
  const workbookXml = dec.decode(workbookEntry);
  const relsBytes = xlsxReadZipEntryData(zip, "xl/_rels/workbook.xml.rels");
  const relsXml = relsBytes ? dec.decode(relsBytes) : null;
  const sheetsMeta = xlsxParseWorkbookSheets(workbookXml, relsXml);
  const sstBytes = xlsxReadZipEntryData(zip, "xl/sharedStrings.xml");
  const sharedStrings = sstBytes ? xlsxParseSharedStrings(dec.decode(sstBytes)) : [];
  const sheets = {};
  const sheetNames = [];
  sheetsMeta.forEach((meta, i)=>{
    let target = meta.target;
    if(!target) target = "worksheets/sheet"+(i+1)+".xml";
    target = target.replace(/^\//,"").replace(/^xl\//,"");
    const path = "xl/"+target;
    const sheetBytes = xlsxReadZipEntryData(zip, path);
    if(!sheetBytes) return;
    const rows = xlsxParseSheetXML(dec.decode(sheetBytes), sharedStrings);
    sheets[meta.name] = rows;
    sheetNames.push(meta.name);
  });
  return {sheetNames, sheets};
}
/* Foglio "Tutti" quando esiste, altrimenti il primo foglio non vuoto (obiettivo A). */
function pickPrimarySheet(workbook){
  if(workbook.sheets["Tutti"]) return {name:"Tutti", rows: workbook.sheets["Tutti"]};
  const name = workbook.sheetNames.find(n=> (workbook.sheets[n]||[]).length>0);
  return name ? {name, rows: workbook.sheets[name]} : {name:null, rows:[]};
}

export {
  XlsxBitReader, xlsxBuildHuffman, xlsxDecodeSymbol, xlsxInflateRaw,
  xlsxReadZipEntries, xlsxReadZipEntryData, xlsxParseSharedStrings,
  xlsxParseWorkbookSheets, xlsxParseSheetXML, xlsxLooksLikeZipSignature,
  classifyImportFile, parseXLSXWorkbook, pickPrimarySheet
};
